import { computed, DestroyRef, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BaseViewModel } from '../base-view-model';
import { ProgressModel } from '../progress/progress.model';
import { FavoriteProduct } from '@common/favorite-grid/model';
import { Constants } from '@helper/constants';
import { RoutePaths } from '@helper/route-paths';
import { FavoriteRepositoryService } from '@service/favorite-repository.service';
import {
  AddStockReminderRequest,
  DeleteStockReminderRequest,
  StockReminder,
} from '@proto/store/api/store/misc/stock_reminder/stock_reminder';
import { MetaData } from '@helper/meta-data';
import { SidebarTab } from '../../misc/sidebar-tab';

import { isEqual } from 'lodash-es';
import { finalize, Observable } from 'rxjs';

@Injectable()
export class StockReminderModel extends BaseViewModel<StockReminder[] | undefined> {
  readonly products = computed(() => this.allProducts()?.slice(0, this.itemCount()), {
    equal: (a, b) => isEqual(a, b),
  });
  readonly itemCount = signal(Constants.displayCount(this.detectService.isMobileDevice()));
  readonly allProducts = computed(() => FavoriteProduct.reminderToProducts(this.apiResult()), {
    equal: (a, b) => isEqual(a, b),
  });

  constructor(
    private lativRepository: FavoriteRepositoryService,
    private progressModel: ProgressModel
  ) {
    super(undefined, true);
  }

  listStockReminders(ref: DestroyRef, callback?: () => void): void {
    this.setApiResult(undefined);
    this.lativRepository
      .listStockReminders()
      .pipe(
        takeUntilDestroyed(ref),
        finalize(() => {
          if (callback) {
            callback();
          }
        })
      )
      .subscribe({
        next: res => this.setApiResult(res.reminders),
        error: err => this.setApiError(err),
      });
  }

  addStockReminder(ref: DestroyRef, skuId: string, phone: string): Observable<unknown> {
    this.progressModel.show();
    return this.lativRepository
      .addStockReminder(
        AddStockReminderRequest.fromPartial({
          skuId,
          phone,
        })
      )
      .pipe(
        takeUntilDestroyed(ref),
        finalize(() => this.progressModel.hide())
      );
  }

  deleteStockReminder(ref: DestroyRef, skuId: string): void {
    this.lativRepository
      .deleteStockReminder(
        DeleteStockReminderRequest.fromPartial({
          skuId,
        })
      )
      .pipe(takeUntilDestroyed(ref))
      .subscribe({
        next: () => this.setApiResult(this.apiResult()?.filter(i => i.skuId != skuId)),
        error: err => this.setApiError(err),
      });
  }

  clearStockReminder(ref: DestroyRef): void {
    this.progressModel.show();
    this.lativRepository
      .clearStockReminder()
      .pipe(
        takeUntilDestroyed(ref),
        finalize(() => this.progressModel.hide())
      )
      .subscribe({
        next: () => this.setApiResult([]),
        error: err => this.setApiError(err),
      });
  }

  override getMetaByUrl(segments: string[]): MetaData | undefined {
    if (segments[0] !== RoutePaths.FAVORITE) {
      return undefined;
    }
    return new MetaData(SidebarTab.favTabTitle);
  }
}
